/**
 * What each image slot in a store expects: its shape, how big it should be,
 * and how a photo for it is framed, checked and previewed.
 *
 * One registry rather than a ratio typed at each field, because the same
 * answer is needed in several places at once - the camera's framing guide, the
 * cropper's locked aspect, the "too small" warning, the in-context preview and
 * the AI studio's output shape. When they each kept their own copy, a banner
 * could be framed 16:9, cropped 3:1 and previewed square.
 *
 * The defaults follow what the storefront actually draws (each entry names the
 * component). A theme that frames a slot differently layers its own values on
 * top through `ImageSpecOverrides` and `resolveImageSpec`; nothing that
 * captures or crops a photo ever needs to know which theme is live.
 */

export interface AspectRatio {
  width: number;
  height: number;
}

export type ImageType =
  | 'PRODUCT'
  | 'CATEGORY'
  | 'LOGO'
  | 'FAVICON'
  | 'HERO_BANNER'
  | 'PROMOTIONAL_BANNER'
  | 'COLLECTION_BANNER'
  | 'SLIDE'
  | 'SECTION_IMAGE'
  | 'GALLERY'
  | 'SERVICE'
  | 'BLOG_COVER'
  | 'SOCIAL'
  | 'AI_SOURCE'
  | 'SIGNATURE';

/** Which storefront mock the preview puts the photo in. */
export type PreviewFrame = 'card' | 'banner' | 'logo' | 'cover' | 'plain';

export interface ImageSpec {
  type: ImageType;
  /** i18n key for the destination name, e.g. 'media.capture.types.PRODUCT' ("Product image"). */
  labelKey: string;
  /** null = free shape (no crop enforced). */
  aspect: AspectRatio | null;
  /** Shapes the merchant may switch between (social, AI source). Empty/undefined = fixed. */
  aspectChoices?: AspectRatio[];
  recommendedWidth: number;
  recommendedHeight: number;
  minWidth: number;
  minHeight: number;
  /** Longest-edge ceiling for the stored source; larger inputs are downscaled, smaller never upscaled. */
  maxEdge: number;
  /** The server's `max_upload_bytes`; a file over it would be refused after the wait. */
  maxFileBytes: number;
  outputFormat: 'image/webp';
  /** Fraction inset of the framing guide's inner safe area (0..0.25). */
  safeArea: number;
  previewFrame: PreviewFrame;
  /** Logo-like: offer AI background removal after capture. */
  suggestBackgroundRemoval?: boolean;
  /** AI studio creative type key for this destination (PRODUCT, BANNER, HERO, PROFILE, CATEGORY, SOCIAL, PROMOTIONAL). */
  aiCreativeType?: string;
}

export type ImageSpecLayer = Partial<Omit<ImageSpec, 'type'>>;
export type ImageSpecOverrides = Partial<Record<ImageType, ImageSpecLayer>>;

/** The backend's default `max_upload_bytes`. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

const SQUARE: AspectRatio = { width: 1, height: 1 };
const FOUR_THREE: AspectRatio = { width: 4, height: 3 };
const FOUR_FIVE: AspectRatio = { width: 4, height: 5 };
const WIDESCREEN: AspectRatio = { width: 16, height: 9 };
const WIDE_BANNER: AspectRatio = { width: 3, height: 1 };

function spec(
  type: ImageType,
  fields: Omit<ImageSpec, 'type' | 'labelKey' | 'maxFileBytes' | 'outputFormat'>,
): ImageSpec {
  return {
    type,
    labelKey: `media.capture.types.${type}`,
    maxFileBytes: MAX_UPLOAD_BYTES,
    outputFormat: 'image/webp',
    ...fields,
  };
}

export const IMAGE_SPECS: Record<ImageType, ImageSpec> = {
  // ProductCard, the listing and search grids and the featured-products
  // section all draw `aspect-square`. The larger ceiling is for the product
  // page's zoom, which is the one place a shopper looks closer than a card.
  PRODUCT: spec('PRODUCT', {
    aspect: SQUARE,
    recommendedWidth: 2000,
    recommendedHeight: 2000,
    minWidth: 800,
    minHeight: 800,
    maxEdge: 2400,
    safeArea: 0.08,
    previewFrame: 'card',
    aiCreativeType: 'PRODUCT',
  }),
  // StorefrontCategoriesPage and the category-grid section: `aspect-[4/3]` tiles.
  CATEGORY: spec('CATEGORY', {
    aspect: FOUR_THREE,
    recommendedWidth: 1600,
    recommendedHeight: 1200,
    minWidth: 800,
    minHeight: 600,
    maxEdge: 2000,
    safeArea: 0.08,
    previewFrame: 'card',
    aiCreativeType: 'CATEGORY',
  }),
  // StoreLogo (layouts/chrome/ChromeParts) draws the logo `object-contain`
  // inside a box capped at 3:1, and the install prompt shows it as a square
  // icon. Square fits both; a wordmark may choose the header's own 3:1.
  LOGO: spec('LOGO', {
    aspect: SQUARE,
    aspectChoices: [SQUARE, WIDE_BANNER],
    recommendedWidth: 1024,
    recommendedHeight: 1024,
    minWidth: 256,
    minHeight: 256,
    maxEdge: 1024,
    safeArea: 0.1,
    previewFrame: 'logo',
    suggestBackgroundRemoval: true,
    aiCreativeType: 'PROFILE',
  }),
  // The browser tab and the PWA manifest icons, whose largest size is 512.
  FAVICON: spec('FAVICON', {
    aspect: SQUARE,
    recommendedWidth: 512,
    recommendedHeight: 512,
    minWidth: 192,
    minHeight: 192,
    maxEdge: 512,
    safeArea: 0.12,
    previewFrame: 'logo',
    suggestBackgroundRemoval: true,
    aiCreativeType: 'PROFILE',
  }),
  // The hero section (SectionRenderer `Hero`) is a full-width `object-cover`
  // backdrop with a minimum height, so its visible shape moves with the
  // screen: about 3:1 on a desktop at the default height, taller on a phone.
  // The wide safe area is what survives that cropping on every screen.
  HERO_BANNER: spec('HERO_BANNER', {
    aspect: WIDE_BANNER,
    recommendedWidth: 1800,
    recommendedHeight: 600,
    minWidth: 1200,
    minHeight: 400,
    maxEdge: 2400,
    safeArea: 0.18,
    previewFrame: 'banner',
    aiCreativeType: 'HERO',
  }),
  // No storefront section draws one yet (the `banner` section is a text strip),
  // so this is the hero's shape with the choices a promotion usually runs in.
  PROMOTIONAL_BANNER: spec('PROMOTIONAL_BANNER', {
    aspect: WIDE_BANNER,
    aspectChoices: [WIDE_BANNER, WIDESCREEN, SQUARE],
    recommendedWidth: 1800,
    recommendedHeight: 600,
    minWidth: 1200,
    minHeight: 400,
    maxEdge: 2400,
    safeArea: 0.12,
    previewFrame: 'banner',
    aiCreativeType: 'PROMOTIONAL',
  }),
  // A strip across the top of a collection; wide by default, 16:9 for themes
  // that give it more height.
  COLLECTION_BANNER: spec('COLLECTION_BANNER', {
    aspect: WIDE_BANNER,
    aspectChoices: [WIDE_BANNER, WIDESCREEN],
    recommendedWidth: 1800,
    recommendedHeight: 600,
    minWidth: 1200,
    minHeight: 400,
    maxEdge: 2400,
    safeArea: 0.12,
    previewFrame: 'banner',
    aiCreativeType: 'BANNER',
  }),
  // SectionRenderer `Slideshow` shares the hero's BANNER_HEIGHT and its
  // `object-cover` backdrop, so a slide is framed exactly like a hero.
  SLIDE: spec('SLIDE', {
    aspect: WIDE_BANNER,
    recommendedWidth: 1800,
    recommendedHeight: 600,
    minWidth: 1200,
    minHeight: 400,
    maxEdge: 2400,
    safeArea: 0.18,
    previewFrame: 'banner',
    aiCreativeType: 'BANNER',
  }),
  // SectionRenderer `ImageWithText`: `aspect-[4/3]` beside the copy.
  SECTION_IMAGE: spec('SECTION_IMAGE', {
    aspect: FOUR_THREE,
    recommendedWidth: 1600,
    recommendedHeight: 1200,
    minWidth: 800,
    minHeight: 600,
    maxEdge: 2000,
    safeArea: 0.08,
    previewFrame: 'plain',
  }),
  // SectionRenderer's gallery section: `aspect-square` tiles.
  GALLERY: spec('GALLERY', {
    aspect: SQUARE,
    recommendedWidth: 1600,
    recommendedHeight: 1600,
    minWidth: 600,
    minHeight: 600,
    maxEdge: 2000,
    safeArea: 0.08,
    previewFrame: 'plain',
  }),
  // SectionRenderer's services section: `aspect-[4/3]` above each service.
  SERVICE: spec('SERVICE', {
    aspect: FOUR_THREE,
    recommendedWidth: 1200,
    recommendedHeight: 900,
    minWidth: 600,
    minHeight: 450,
    maxEdge: 1600,
    safeArea: 0.08,
    previewFrame: 'card',
  }),
  // StorefrontPostPage draws the cover `aspect-[16/9]`; the blog list's cards
  // are `aspect-[16/10]`, which trims about 5% off each side - inside the
  // safe area, so nothing that matters is lost on either.
  BLOG_COVER: spec('BLOG_COVER', {
    aspect: WIDESCREEN,
    recommendedWidth: 1600,
    recommendedHeight: 900,
    minWidth: 960,
    minHeight: 540,
    maxEdge: 2000,
    safeArea: 0.08,
    previewFrame: 'cover',
    aiCreativeType: 'BANNER',
  }),
  // Not drawn by the storefront: posts leave the store, so the merchant picks
  // the shape of the feed it is going to.
  SOCIAL: spec('SOCIAL', {
    aspect: SQUARE,
    aspectChoices: [SQUARE, FOUR_FIVE, WIDESCREEN],
    recommendedWidth: 1080,
    recommendedHeight: 1080,
    minWidth: 600,
    minHeight: 600,
    maxEdge: 2160,
    safeArea: 0.06,
    previewFrame: 'plain',
    aiCreativeType: 'SOCIAL',
  }),
  // The photo the AI studio works from. Free by default - the studio decides
  // the output shape - with the common shapes one tap away.
  AI_SOURCE: spec('AI_SOURCE', {
    aspect: null,
    aspectChoices: [SQUARE, FOUR_FIVE, WIDESCREEN, WIDE_BANNER],
    recommendedWidth: 2048,
    recommendedHeight: 2048,
    minWidth: 512,
    minHeight: 512,
    maxEdge: 2048,
    safeArea: 0.05,
    previewFrame: 'plain',
  }),
  // The signature on invoices, receipts and emails. Not a storefront slot: the
  // PDF scales it into a 45x16 mm box and an email draws it about 200 pixels
  // wide, so a free shape no longer than 400 pixels is all it needs. Kept
  // transparent - WebP (or PNG where WebP cannot be written) holds the alpha a
  // drawn or scanned signature relies on to sit on any background.
  SIGNATURE: spec('SIGNATURE', {
    aspect: null,
    recommendedWidth: 400,
    recommendedHeight: 150,
    minWidth: 120,
    minHeight: 40,
    maxEdge: 400,
    safeArea: 0,
    previewFrame: 'plain',
  }),
};

/**
 * The default spec for `type` with each layer laid over it in order - a theme's
 * overrides, then a single field's.
 *
 * A key present but `undefined` is skipped rather than applied, so a layer
 * built with optional fields cannot erase a default by accident. `null` is a
 * real value (a free aspect) and does apply.
 */
export function resolveImageSpec(
  type: ImageType,
  ...layers: (ImageSpecLayer | undefined)[]
): ImageSpec {
  const resolved: ImageSpec = { ...IMAGE_SPECS[type] };
  for (const layer of layers) {
    if (!layer) continue;
    for (const [key, value] of Object.entries(layer)) {
      if (value !== undefined) (resolved as unknown as Record<string, unknown>)[key] = value;
    }
  }
  resolved.type = type;
  return resolved;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * A ratio from how people and theme files write one: `'3:1'`, `'16/9'`,
 * `'4x5'` or a plain number such as `'1.5'`. Anything else - including
 * `'free'` and `''` - is `null`, meaning no shape is enforced.
 *
 * Whole-number ratios are reduced (`1920:1080` is `16:9`) so two spellings of
 * the same shape compare equal; a decimal becomes the smallest whole ratio
 * that matches it closely.
 */
export function parseAspect(value: string): AspectRatio | null {
  const text = value.trim();
  const pair = /^(\d+(?:\.\d+)?)\s*[:/x×]\s*(\d+(?:\.\d+)?)$/i.exec(text);
  if (pair) {
    const width = Number(pair[1]);
    const height = Number(pair[2]);
    if (!(width > 0 && height > 0)) return null;
    if (Number.isInteger(width) && Number.isInteger(height)) {
      const divisor = gcd(width, height);
      return { width: width / divisor, height: height / divisor };
    }
    return fromDecimal(width / height);
  }
  if (/^\d+(?:\.\d+)?$/.test(text)) {
    const ratio = Number(text);
    return ratio > 0 ? fromDecimal(ratio) : null;
  }
  return null;
}

function fromDecimal(ratio: number): AspectRatio | null {
  for (let height = 1; height <= 100; height += 1) {
    const width = Math.round(ratio * height);
    if (width > 0 && Math.abs(width / height - ratio) < 0.001) return { width, height };
  }
  return null;
}

/** `'3:1'`, or `''` for a free shape. */
export function formatAspect(aspect: AspectRatio | null): string {
  return aspect ? `${aspect.width}:${aspect.height}` : '';
}

/** Width ÷ height, the form `cropRect` works in. */
export function aspectValue(aspect: AspectRatio | null): number | null {
  return aspect ? aspect.width / aspect.height : null;
}

/** Whether two ratios describe the same shape, however they were written. */
export function sameAspect(a: AspectRatio | null, b: AspectRatio | null): boolean {
  if (!a || !b) return a === b;
  return a.width * b.height === b.width * a.height;
}

/**
 * The spec as it applies once the merchant has picked `aspect` from its
 * choices.
 *
 * The recommended and minimum sizes were written for the default shape, so
 * they are re-derived for the new one keeping the long edge: a 1080 square
 * post becomes a 1080-tall 4:5, not a 1080-wide one that would demand more
 * pixels than the feed shows.
 */
export function specForAspect(base: ImageSpec, aspect: AspectRatio | null): ImageSpec {
  if (sameAspect(base.aspect, aspect)) return base;
  if (!aspect) return { ...base, aspect: null };

  const reshape = (width: number, height: number) => {
    const edge = Math.max(width, height);
    return aspect.width >= aspect.height
      ? { width: edge, height: Math.round((edge * aspect.height) / aspect.width) }
      : { width: Math.round((edge * aspect.width) / aspect.height), height: edge };
  };
  const recommended = reshape(base.recommendedWidth, base.recommendedHeight);
  const minimum = reshape(base.minWidth, base.minHeight);
  return {
    ...base,
    aspect,
    recommendedWidth: recommended.width,
    recommendedHeight: recommended.height,
    minWidth: minimum.width,
    minHeight: minimum.height,
  };
}

/**
 * The shapes a chooser should offer for `base`, in order: a free shape first
 * when that is the default, then the default, then the other choices. Empty
 * when the slot has one fixed shape and there is nothing to choose.
 */
export function aspectOptions(base: ImageSpec): (AspectRatio | null)[] {
  const choices = base.aspectChoices ?? [];
  if (choices.length === 0) return [];
  const options: (AspectRatio | null)[] = [base.aspect];
  for (const choice of choices) {
    if (!options.some((option) => sameAspect(option, choice))) options.push(choice);
  }
  return options;
}

/**
 * The candidate whose ratio is closest to `target`, measured on a log scale so
 * 2:1 is as far from 1:1 as 1:2 is. Candidates whose ratio is unknown are
 * skipped; `null` when none is known.
 */
export function nearestAspect<T>(
  candidates: readonly T[],
  target: AspectRatio,
  ratioOf: (candidate: T) => number | null,
): T | null {
  const wanted = Math.log(target.width / target.height);
  let best: T | null = null;
  let bestDistance = Infinity;
  for (const candidate of candidates) {
    const ratio = ratioOf(candidate);
    if (!ratio || ratio <= 0) continue;
    const distance = Math.abs(Math.log(ratio) - wanted);
    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
    }
  }
  return best;
}
